import Cookies from 'js-cookie';
import {
  COOKIE_TRANSFER_REQUIRED,
  COOKIE_TRANSFER_SERVICE
} from '../compat/config';
import { PermissionClientSettings } from '../interfaces';
import * as Logger from '../../common/tools/logger';
import { BRAND_DOMAIN } from '../../common/tools/util';

export const COOKIE_IDENTIFIER_TEST = '__ppp__';

/**
 * @function setCookiePublisherPortal
 * @description Set the publishers portal for the cookie module
 * @param {PermissionClientSettings['portal']} publisherPortal
 * @deprecated
 * @return {void}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function setCookiePublisherPortal(publisherPortal: PermissionClientSettings['portal']): void {
  Logger.warn('The setCookiePublisherPortal method is deprecated (no replacement).');
}

/**
 * @function cookiesSupported
 * @description Check if cookies are supported by the client (f. e. if blocked)
 * @return {boolean}
 */
export function cookiesSupported(): boolean {
  try {
    Cookies.set(COOKIE_IDENTIFIER_TEST, '1');
    const val = Cookies.get(COOKIE_IDENTIFIER_TEST);
    Cookies.remove(COOKIE_IDENTIFIER_TEST);
    return val === '1';
  } catch (ex) {
    return false;
  }
}

/**
 * @function createCookieTransporter
 * @description Creates a new iframe to transfer cookies to another domain
 * @param {string} transportSrc The source of the transporter (domain context to transfer cookie to)
 * @param {string} cName The cookie name
 * @param {string} cValue The cookie value
 * @param {number} cExpire Expire date in days for the cookie
 * @return {Promise<void>}
 */
async function createCookieTransporter(transportSrc: string, cName: string, cValue: string, cExpire: number): Promise<void> {
  /**
   * Helper function to cleanup the iframe(s) after finished transferring
   */
  const cleanup = (iframe: HTMLIFrameElement): void => {
    try {
      iframe.remove();
    } catch (error) {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }
  }

  const iSrc = `${transportSrc}?cName=${cName}&cValue=${cValue}&cExpire=${cExpire}`;
  const iElem: HTMLIFrameElement = document.createElement('iframe');
    iElem.src = iSrc;
    iElem.width = '0';
    iElem.height = '0';
    iElem.frameBorder = '0';
    iElem.style.display = 'none';
    iElem.style.visibility = 'hidden';
    iElem.onload = (): void => {
      cleanup(iElem);
      Promise.resolve();
    }
    iElem.onerror = (): void => {
      cleanup(iElem);
      Promise.reject(`Failed to transfer cookie.`);
    }
    document.body.appendChild(iElem);
}

/**
 * @function transferCookie
 * @description Helper function to determine, where to transfer cookies to (GMX.AT / GMX.CH -> GMX.NET | GMX.NET -> GMX.AT & GMX.CH)
 * @param {string} cName The cookie name
 * @param {string} cValue The cookie value
 * @param {number} cExpire Expire date in days for the cookie
 * @return {Promise<void>}
 */
export async function transferCookie(cName: string, cValue: string, cExpire: number): Promise<void> {
  if (!COOKIE_TRANSFER_REQUIRED) {
    return;
  }

  if (BRAND_DOMAIN === 'gmx.net') {
    await Promise.all([
      createCookieTransporter(COOKIE_TRANSFER_SERVICE.gmxat, cName, cValue, cExpire),
      createCookieTransporter(COOKIE_TRANSFER_SERVICE.gmxch, cName, cValue, cExpire)
    ]).catch((error: Error) => {
      Promise.reject(error);
    });
  } else if (BRAND_DOMAIN === 'gmx.at' || BRAND_DOMAIN === 'gmx.ch') {
    await createCookieTransporter(COOKIE_TRANSFER_SERVICE.gmx, cName, cValue, cExpire)
      .catch((error: Error) => {
        Promise.reject(error);
      });
  }
}
